import { useState, useEffect } from 'react';
import { client } from '@/api/client';
import { Printer, FileDown } from 'lucide-react';
import DateRangeFilter from './DateRangeFilter';
import { exportCSV, inRange } from './reportUtils';
import { useCompanyInfo, generatePrintHTML, openPrint } from './ReportExporter';

export default function DriverSettlementsReport() {
  const [settlements, setSettlements] = useState([]);
  const [loads, setLoads] = useState([]);
  const [driverFilter, setDriverFilter] = useState('');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [loading, setLoading] = useState(true);
  const company = useCompanyInfo();

  useEffect(() => {
    Promise.all([
      client.entities.Settlement.list('-settlement_date', 500),
      client.entities.Load.list('-delivery_date', 500),
    ]).then(([s, l]) => { setSettlements(s); setLoads(l); setLoading(false); });
  }, []);

  const drivers = [...new Set(settlements.map(s => s.driver_name).filter(Boolean))].sort();

  const filtered = settlements.filter(s =>
    inRange(s.settlement_date || s.finalized_date || s.created_date, start, end) &&
    (!driverFilter || s.driver_name === driverFilter)
  );

  const getLoad = (id) => loads.find(l => l.id === id);

  const totals = {
    gross: filtered.reduce((s, r) => s + (r.gross_revenue || 0), 0),
    carrier: filtered.reduce((s, r) => s + (r.carrier_take_amount || 0), 0),
    fuel: filtered.reduce((s, r) => s + (r.fuel_advance_total || 0), 0),
    dispatch: filtered.reduce((s, r) => s + (r.dispatch_fee_amount || 0), 0),
    // "Deductions" column below was actually only ever showing dispatch
    // fee (mislabeled) with lumper/late fees shown nowhere -- Gross -
    // Carrier - Fuel - "Deductions" never added up to Net Pay whenever
    // either was charged. Split into an honestly-labeled Dispatch Fee
    // column plus a new Other Fees column for lumper/late.
    otherFees: filtered.reduce((s, r) => s + (r.lumper_fee_deducted || 0) + (r.late_fee_deducted || 0), 0),
    net: filtered.reduce((s, r) => s + (r.final_net_pay ?? r.net_driver_pay ?? 0), 0),
  };

  const csvRows = filtered.map(s => {
    const l = getLoad(s.load_id);
    return {
      Driver: s.driver_name || '',
      'Load #': s.load_number || '',
      'Pickup Date': l?.pickup_date || '',
      'Delivery Date': l?.delivery_date || '',
      Gross: (s.gross_revenue || 0).toFixed(2),
      'Carrier Fee': (s.carrier_take_amount || 0).toFixed(2),
      'Fuel Advance': (s.fuel_advance_total || 0).toFixed(2),
      'Dispatch Fee': (s.dispatch_fee_amount || 0).toFixed(2),
      'Other Fees': ((s.lumper_fee_deducted || 0) + (s.late_fee_deducted || 0)).toFixed(2),
      'Net Pay': (s.final_net_pay ?? s.net_driver_pay ?? 0).toFixed(2),
      Status: s.status || '',
    };
  });

  const handlePrint = () => {
    const cols = ['Driver', 'Load #', 'Pickup Date', 'Delivery Date', 'Gross', 'Carrier Fee', 'Fuel Advance', 'Dispatch Fee', 'Other Fees', 'Net Pay', 'Status'];
    const dataRows = filtered.map(s => {
      const l = getLoad(s.load_id);
      return [
        { value: s.driver_name || '', class: 'bold' }, `#${s.load_number || ''}`,
        l?.pickup_date || '—', l?.delivery_date || '—',
        { value: `$${(s.gross_revenue || 0).toLocaleString()}`, class: 'positive' },
        { value: `-$${(s.carrier_take_amount || 0).toFixed(2)}`, class: 'negative' },
        { value: `-$${(s.fuel_advance_total || 0).toFixed(2)}`, class: 'negative' },
        { value: `-$${(s.dispatch_fee_amount || 0).toFixed(2)}`, class: 'negative' },
        { value: `-$${((s.lumper_fee_deducted || 0) + (s.late_fee_deducted || 0)).toFixed(2)}`, class: 'negative' },
        { value: `$${(s.final_net_pay ?? s.net_driver_pay ?? 0).toFixed(2)}`, class: 'positive' },
        s.status || '',
      ];
    });
    const totalsRow = [
      { value: `TOTALS (${filtered.length})`, class: '' }, '', '', '',
      { value: `$${totals.gross.toLocaleString()}`, class: 'gold' },
      { value: `-$${totals.carrier.toFixed(2)}`, class: '' },
      { value: `-$${totals.fuel.toFixed(2)}`, class: '' },
      { value: `-$${totals.dispatch.toFixed(2)}`, class: '' },
      { value: `-$${totals.otherFees.toFixed(2)}`, class: '' },
      { value: `$${totals.net.toFixed(2)}`, class: 'gold' }, '',
    ];
    const cards = [
      { label: 'Total Settlements', value: filtered.length, color: 'gold' },
      { label: 'Gross Revenue', value: `$${totals.gross.toLocaleString()}`, color: 'positive' },
      { label: 'Carrier Fees', value: `$${totals.carrier.toFixed(2)}`, color: 'negative' },
      { label: 'Net Driver Pay', value: `$${totals.net.toFixed(2)}`, color: 'positive' },
    ];
    const html = generatePrintHTML('Driver Settlements Report', `${start || 'All'} – ${end || 'All'}`, cols, dataRows, totalsRow, company, cards);
    openPrint(html);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <DateRangeFilter onChange={(s, e) => { setStart(s); setEnd(e); }} onExport={() => exportCSV(csvRows, 'driver-settlements.csv')} exportDisabled={loading || filtered.length === 0} />
        <button onClick={handlePrint} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 border border-border text-sm text-foreground hover:bg-white/10"><Printer className="w-4 h-4" /> Print</button>
        <button onClick={handlePrint} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 border border-border text-sm text-foreground hover:bg-white/10"><FileDown className="w-4 h-4" /> Export PDF</button>
      </div>
      <div className="flex items-center gap-2">
        <label className="text-xs text-muted-foreground">Driver:</label>
        <select value={driverFilter} onChange={e => setDriverFilter(e.target.value)}
          className="px-3 py-1.5 rounded-lg bg-transparent border border-input text-foreground text-xs">
          <option value="" className="bg-background">All Drivers</option>
          {drivers.map(d => <option key={d} value={d} className="bg-background">{d}</option>)}
        </select>
      </div>
      <div className="glass-card overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-border">
              {['Driver', 'Load #', 'Pickup Date', 'Delivery Date', 'Gross', 'Carrier Fee', 'Fuel Advance', 'Dispatch Fee', 'Other Fees', 'Net Pay', 'Status'].map(h => (
                <th key={h} className="text-left px-3 py-2.5 text-muted-foreground font-medium whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {filtered.map(s => {
              const l = getLoad(s.load_id);
              return (
                <tr key={s.id} className="hover:bg-white/3">
                  <td className="px-3 py-2.5 font-medium text-foreground">{s.driver_name}</td>
                  <td className="px-3 py-2.5 text-primary">#{s.load_number}</td>
                  <td className="px-3 py-2.5 text-muted-foreground">{l?.pickup_date || '—'}</td>
                  <td className="px-3 py-2.5 text-muted-foreground">{l?.delivery_date || '—'}</td>
                  <td className="px-3 py-2.5 font-semibold text-foreground">${(s.gross_revenue || 0).toLocaleString()}</td>
                  <td className="px-3 py-2.5 text-red-400">−${(s.carrier_take_amount || 0).toFixed(2)}</td>
                  <td className="px-3 py-2.5 text-amber-400">−${(s.fuel_advance_total || 0).toFixed(2)}</td>
                  <td className="px-3 py-2.5 text-red-400">−${(s.dispatch_fee_amount || 0).toFixed(2)}</td>
                  <td className="px-3 py-2.5 text-red-400">−${((s.lumper_fee_deducted || 0) + (s.late_fee_deducted || 0)).toFixed(2)}</td>
                  <td className="px-3 py-2.5 font-bold text-green-400">${(s.final_net_pay ?? s.net_driver_pay ?? 0).toFixed(2)}</td>
                  <td className="px-3 py-2.5 text-muted-foreground">{s.status}</td>
                </tr>
              );
            })}
          </tbody>
          {filtered.length > 0 && (
            <tfoot>
              <tr className="border-t border-border bg-white/5 font-bold">
                <td colSpan={4} className="px-3 py-2.5 text-foreground">TOTALS ({filtered.length} settlements)</td>
                <td className="px-3 py-2.5 text-foreground">${totals.gross.toLocaleString()}</td>
                <td className="px-3 py-2.5 text-red-400">−${totals.carrier.toFixed(2)}</td>
                <td className="px-3 py-2.5 text-amber-400">−${totals.fuel.toFixed(2)}</td>
                <td className="px-3 py-2.5 text-red-400">−${totals.dispatch.toFixed(2)}</td>
                <td className="px-3 py-2.5 text-red-400">−${totals.otherFees.toFixed(2)}</td>
                <td className="px-3 py-2.5 text-green-400">${totals.net.toFixed(2)}</td>
                <td></td>
              </tr>
            </tfoot>
          )}
        </table>
        {filtered.length === 0 && !loading && <p className="text-center text-muted-foreground py-8 text-sm">No settlements found for this period.</p>}
        {loading && <p className="text-center text-muted-foreground py-8 text-sm">Loading...</p>}
      </div>
    </div>
  );
}